<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <!-- Contenido Principal - ajustado para tener margen izquierdo en escritorio -->
    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Académico / Boleta</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <!-- Selector de periodos -->
          <PeriodSelector 
            v-if="studentId"
            :studentId="studentId"
            v-model="selectedPeriod"
            @period-changed="loadGrades"
          />

          <!-- Tabla de calificaciones -->
          <div v-if="loadingGrades" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          
          <div v-else-if="errorGrades" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ errorGrades }}
          </div>
          
          <div v-else-if="grades && grades.length > 0">
            <h2 class="text-lg font-semibold mb-2">Calificaciones del periodo {{ selectedPeriod }}:</h2>
            
            <!-- Tabla responsiva para dispositivos móviles -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <div class="grid grid-cols-1 gap-4 sm:hidden">
                <!-- Vista móvil: tarjetas individuales para cada materia -->
                <div 
                  v-for="grade in grades" 
                  :key="grade.code_subject"
                  class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 border-blue-500"
                >
                  <div class="flex items-center justify-between mb-2">
                    <h3 class="font-bold text-lg">{{ grade.Materia }}</h3>
                  </div>
                  <div class="grid grid-cols-2 gap-y-2">
                    <div class="text-sm font-medium text-gray-500">Parcial 1:</div>
                    <div class="text-sm font-bold">{{ grade.Parcial1 || '-' }}</div>
                    
                    <div class="text-sm font-medium text-gray-500">Parcial 2:</div>
                    <div class="text-sm font-bold">{{ grade.Parcial2 || '-' }}</div>
                    
                    <div class="text-sm font-medium text-gray-500">Parcial 3:</div>
                    <div class="text-sm font-bold">{{ grade.Parcial3 || '-' }}</div>
                    
                    <div class="text-sm font-medium text-gray-500">Ordinario:</div>
                    <div class="text-sm font-bold">{{ grade.Ordinario || '-' }}</div>
                    
                    <div class="text-sm font-medium text-gray-500">Final:</div>
                    <div class="text-sm font-bold">{{ grade.Final || '-' }}</div>
                    
                    <div class="text-sm font-medium text-gray-500">Extra-Ordinario:</div>
                    <div class="text-sm font-bold">{{ grade['Extra-Ordinario'] || '-' }}</div>
                  </div>

                  <!-- Botones de acción para móvil -->
                  <div class="flex flex-col gap-2 mt-4 pt-3 border-t border-gray-200">
                    <!-- Inconformidad -->
                    <button @click="openDisagreementModal(grade)"
                      class="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none transition-colors">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Inconformidad
                    </button>
                    <!-- ★ BOTÓN EVALUACIÓN DOCENTE (móvil) -->
                    <button
                      @click="irEvaluacionDocente(grade)"
                      :disabled="!periodoFinalizado(grade)"
                      :title="!periodoFinalizado(grade) ? 'La evaluación docente en boleta está disponible al finalizar el periodo' : 'Evaluar docente'"
                      class="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white focus:outline-none transition-colors"
                      :class="periodoFinalizado(grade) ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                      {{ periodoFinalizado(grade) ? 'Evaluar docente' : 'Evaluación no disponible' }}
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Vista escritorio: tabla tradicional -->
              <table class="min-w-full divide-y divide-gray-300 hidden sm:table">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Materia</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Parcial 1</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Parcial 2</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Parcial 3</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Ordinario</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Final</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Extra</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Acciones</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                  <tr v-for="grade in grades" :key="grade.code_subject">
                    <td class="whitespace-normal py-4 pl-4 pr-3 text-sm font-medium text-gray-900">{{ grade.Materia }}</td>
                    <td class="px-3 py-4 text-sm text-center">{{ grade.Parcial1 || '-' }}</td>
                    <td class="px-3 py-4 text-sm text-center">{{ grade.Parcial2 || '-' }}</td>
                    <td class="px-3 py-4 text-sm text-center">{{ grade.Parcial3 || '-' }}</td>
                    <td class="px-3 py-4 text-sm text-center">{{ grade.Ordinario || '-' }}</td>
                    <td class="px-3 py-4 text-sm text-center">{{ grade.Final || '-' }}</td>
                    <td class="px-3 py-4 text-sm text-center">{{ grade['Extra-Ordinario'] || '-' }}</td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      <div class="buttons-container flex items-center justify-center gap-2">
                        <!-- Inconformidad -->
                        <div class="tooltip-wrapper">
                          <button @click="openDisagreementModal(grade)"
                            class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-details">
                            <span class="tooltip-text">Reportar inconformidad</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10"><path d="M8 0L0 10H16L8 0Z" fill="currentColor"/></svg>
                          </div>
                        </div>
                        <!-- ★ BOTÓN EVALUACIÓN DOCENTE (escritorio) -->
                        <div class="tooltip-wrapper">
                          <button
                            @click="irEvaluacionDocente(grade)"
                            :disabled="!periodoFinalizado(grade)"
                            class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none transition-colors"
                            :class="periodoFinalizado(grade) ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-details">
                            <span class="tooltip-text">{{ periodoFinalizado(grade) ? 'Evaluar docente' : 'Evaluación no disponible' }}</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10"><path d="M8 0L0 10H16L8 0Z" fill="currentColor"/></svg>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-else-if="selectedPeriod" class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            No hay calificaciones disponibles para el periodo seleccionado.
          </div>
        </div>
      </main>
    </div>

    <!-- Modal de Inconformidad -->
    <DisagreementModal
      :isVisible="showDisagreementModal"
      :selectedSubject="selectedSubjectForDisagreement"
      :studentId="studentId"
      :selectedPeriod="selectedPeriod"
      @close="closeDisagreementModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import PeriodSelector from '@/components/PeriodSelector.vue';
import DisagreementModal from '@/components/InconformidadModal.vue';
import { getGrades } from "@/services/api";

//Modo pruebas para forzar la activación del botón de evaluación docente en boleta
const DEBUG = {
  FORCE_ENABLE_FOR_TESTING: false,
};

export default {
  components: { SidebarMenu, PeriodSelector, DisagreementModal },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      studentId: null,
      selectedPeriod: '',
      grades: null,
      loadingGrades: false,
      errorGrades: null,
      showDisagreementModal: false,
      selectedSubjectForDisagreement: null,
      // Mapa de calificaciones enriquecidas con fechas de periodo
      // (se llena en loadGrades si tu API devuelve date_end por materia)
      gradePeriodDates: {},
    };
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    // Navegar a evaluación docente con datos preseleccionados
    irEvaluacionDocente(grade) {
      this.$router.push({
        name: 'EvaluacionDocente',
        query: {
          periodo: this.selectedPeriod,
          codigo_materia: grade.code_subject,
          nombre_materia: grade.Materia,
          nombre_docente: grade.teacher || grade.Docente || '',
          // fecha_inicio y fecha_fin opcionales: si tu API los devuelve en grade
          fecha_inicio: grade.date_start || '',
          fecha_fin: grade.date_end || '',
        }
      });
    },
    //En Boleta el botón se activa cuando el periodo ya TERMINÓ
    periodoFinalizado(grade) {
      if (DEBUG.FORCE_ENABLE_FOR_TESTING) return true;
      // Si tu API devuelve date_end por materia, úsalo:
      if (grade.date_end) return new Date(grade.date_end) < new Date();
      // Fallback: si no tienes fecha, deja desactivado por seguridad
      return false;
    },
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];
        } else {
          this.errorGrades = "No se pudo extraer el ID de estudiante del email.";
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadGrades(periodId) {
      if (!periodId) return;
      this.loadingGrades = true;
      this.errorGrades = null;
      this.grades = null;
      try {
        const response = await getGrades({ idstudent: this.studentId, idperiod: periodId });
        this.grades = response.data;
      } catch (error) {
        this.errorGrades = "Error al cargar las calificaciones. Por favor, intente nuevamente.";
      } finally {
        this.loadingGrades = false;
      }
    },
    openDisagreementModal(grade) { this.selectedSubjectForDisagreement = grade; this.showDisagreementModal = true; },
    closeDisagreementModal() { this.showDisagreementModal = false; this.selectedSubjectForDisagreement = null; },
    toggleMenu() { this.isMenuOpen = !this.isMenuOpen; },
    closeMenu() { this.isMenuOpen = false; },
    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await new Promise(r => setTimeout(r, 100));
        await axios.post('/logout', {}, { withCredentials: true, headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } });
        document.cookie.split(";").forEach(c => { document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); });
        this.$router.push('/login');
      } catch (error) { console.error('Error al cerrar sesión:', error.response?.data || error); }
    }
  }
};
</script>